import { useEffect, useState } from "react"
import { IconLockFill18, IconLockOpenFill18 } from "nucleo-ui-fill-18"
import {
	IconClockOutline18 as ClockIcon,
	IconWindowBottomOutline18 as DockIcon,
	IconArrowDoorOutOutline18 as ExitIcon,
} from "nucleo-ui-outline-18"

import type { SessionData } from "@productlane/auth"
import { cn } from "@productlane/lib/src/cn"
import { Avatar, Badge, Button, StripeIcon, Tooltip } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { BAR_BUTTON, BAR_ICON } from "./bar-styles"
import {
	formatElapsed,
	IMPERSONATION_BLOCKED_EVENT,
	parsePageContext,
	stripeDashboardUrl,
} from "./impersonation-context"
import { ImpersonationCopyMenu } from "./impersonation-copy-menu"
import { ImpersonationMemberMenu } from "./impersonation-member-menu"
import { SupportDock } from "./support-dock"

const BLOCKED_TOAST_ID = "impersonation-read-only"

function useNow(intervalMs: number) {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		const id = window.setInterval(() => setNow(Date.now()), intervalMs)
		return () => window.clearInterval(id)
	}, [intervalMs])
	return now
}

// Shrinks every viewport-height layout so nothing sits under the bar.
function useReserveBarSpace() {
	useEffect(() => {
		document.documentElement.dataset.impersonating = ""
		return () => {
			delete document.documentElement.dataset.impersonating
		}
	}, [])
}

function Separator() {
	return <span aria-hidden className="h-4 w-px flex-none bg-amber-900/20" />
}

export function ImpersonationBar() {
	const { session } = useSession()
	if (!session?.impersonatingFromUserId) return null
	return <ImpersonationBarInner session={session} />
}

function ImpersonationBarInner({ session }: { session: SessionData }) {
	useReserveBarSpace()
	const now = useNow(30_000)
	const [dockOpen, setDockOpen] = useState(false)
	const utils = api.useUtils()
	// The lock flips on click; the server confirms after.
	const [pendingWrites, setPendingWrites] = useState<boolean | null>(null)
	const writesAllowed =
		pendingWrites ?? session.impersonationWritesAllowed === true
	const workspaceName = session.workspaceName

	const summaryQuery = api.user.impersonationSummary.useQuery(undefined, {
		staleTime: 5 * 60_000,
	})
	const summary = summaryQuery.data
	const stripeUrl = stripeDashboardUrl(
		summary?.stripeSubscriptionId,
		summary?.stripeCustomerId,
	)

	const writesMutation = api.user.setImpersonationWrites.useMutation({
		onMutate: ({ allowed }) => setPendingWrites(allowed),
		onSuccess: ({ allowed }) => {
			utils.user.info.setData(undefined, (prev) =>
				prev ? { ...prev, impersonationWritesAllowed: allowed } : prev,
			)
			void utils.user.info.invalidate()
			if (allowed) {
				toast.info({
					title: `Writes on for ${workspaceName}`,
					description: "Every change now goes to the customer's real data.",
					icon: IconLockOpenFill18,
					action: {
						label: "Lock",
						callback: () => writesMutation.mutate({ allowed: false }),
					},
				})
			} else {
				toast.success({ title: "Read only again", icon: IconLockFill18 })
			}
		},
		onError: (err) => {
			setPendingWrites(null)
			toast.error({ title: err.message, icon: IconLockFill18 })
		},
	})

	const sessionWrites = session.impersonationWritesAllowed === true
	useEffect(() => {
		if (pendingWrites === sessionWrites) setPendingWrites(null)
	}, [pendingWrites, sessionWrites])

	const stopMutation = api.user.stopImpersonation.useMutation({
		onSuccess: async (data) => {
			await utils.user.info.reset()
			window.location.replace(data?.returnTo ?? "/threads")
		},
		onError: (err) => {
			toast.error({ title: err.message, icon: ExitIcon })
		},
	})

	const setWrites = writesMutation.mutate
	useEffect(() => {
		const onBlocked = () => {
			toast.info({
				id: BLOCKED_TOAST_ID,
				title: "Read only while impersonating",
				description: `Allowing writes changes ${workspaceName}'s real data.`,
				icon: IconLockFill18,
				action: {
					label: "Allow writes",
					callback: () => setWrites({ allowed: true }),
				},
			})
		}
		window.addEventListener(IMPERSONATION_BLOCKED_EVENT, onBlocked)
		return () =>
			window.removeEventListener(IMPERSONATION_BLOCKED_EVENT, onBlocked)
	}, [workspaceName, setWrites])

	const returnsToThread =
		parsePageContext(session.impersonationReturnTo ?? "")?.entity === "thread"

	const lockTooltip = (
		<div className="flex max-w-64 flex-col gap-1 text-left">
			<span className="font-medium">
				{writesAllowed
					? "Writes on. Click to lock."
					: "Read only. Click to allow writes."}
			</span>
			<span className="opacity-70">
				{writesAllowed
					? `Every change goes to ${workspaceName}'s real settings and data.`
					: `Writes change ${workspaceName}'s settings and data, and can message their customers.`}
			</span>
		</div>
	)

	return (
		<>
			<div
				aria-hidden
				className="pointer-events-none fixed inset-0 z-[46] ring-2 ring-inset ring-amber-400"
			/>
			{dockOpen ? (
				<SupportDock
					session={session}
					stripeUrl={stripeUrl}
					onClose={() => setDockOpen(false)}
				/>
			) : null}
			<div
				role="region"
				aria-label="Impersonation"
				className="fixed inset-x-0 bottom-0 z-[45] flex h-10 cursor-default select-none items-center gap-2.5 bg-amber-300 pl-2 pr-2 text-amber-950 shadow-[inset_0_0.5px_0_rgba(120,53,15,0.16)]"
			>
				<Tooltip label={lockTooltip} side="top">
					<Button
						size="sm"
						variant="ghost"
						Icon={writesAllowed ? IconLockOpenFill18 : IconLockFill18}
						iconClassName="text-current"
						aria-pressed={writesAllowed}
						className={cn(
							"min-w-[6.5rem] justify-start",
							writesAllowed
								? "bg-amber-950 text-amber-50 hover:bg-amber-900 hover:text-amber-50 dark:bg-amber-950 dark:text-amber-50 dark:hover:bg-amber-900 dark:hover:text-amber-50"
								: cn(BAR_BUTTON, "bg-white/55"),
						)}
						onClick={() => {
							if (writesMutation.isLoading) return
							writesMutation.mutate({ allowed: !writesAllowed })
						}}
					>
						{writesAllowed ? "Writes on" : "Read only"}
					</Button>
				</Tooltip>

				<span className="flex-none text-xs font-medium text-amber-900">
					Viewing as
				</span>
				<ImpersonationMemberMenu session={session} />
				<Separator />
				<div className="flex min-w-0 items-center gap-1.5">
					<Avatar
						size="xxs+"
						roundness="md"
						name={workspaceName}
						image={session.workspaceLogoUrl}
						className="flex-none"
					/>
					<span className="max-w-[12rem] truncate text-[13px] font-medium">
						{workspaceName}
					</span>
					{summary?.plan ? (
						<Badge size="sm" variant="on-amber">
							{summary.plan}
						</Badge>
					) : null}
				</div>
				<Separator />
				<span className="flex min-w-[4.5rem] flex-none items-center gap-1 text-xs font-medium tabular-nums text-amber-900">
					<ClockIcon className="h-3.5 w-3.5 flex-none" />
					{formatElapsed(session.impersonationStartedAt, now) ?? "Now"}
				</span>

				<div className="ml-auto flex flex-none items-center gap-0.5">
					<Button
						variant="ghost"
						size="sm"
						Icon={DockIcon}
						iconClassName={BAR_ICON}
						aria-expanded={dockOpen}
						className={cn(BAR_BUTTON, dockOpen && "bg-white/60")}
						onClick={() => setDockOpen((open) => !open)}
					>
						Dock
					</Button>
					<ImpersonationCopyMenu
						session={session}
						plan={summary?.plan}
						stripeCustomerId={summary?.stripeCustomerId}
					/>
					{stripeUrl ? (
						<Button
							variant="ghost"
							size="sm"
							Icon={StripeIcon}
							iconClassName={BAR_ICON}
							className={BAR_BUTTON}
							onClick={() => window.open(stripeUrl, "_blank", "noopener")}
						>
							Stripe
						</Button>
					) : null}
					<Button
						size="sm"
						Icon={ExitIcon}
						isLoading={stopMutation.isLoading}
						className="ml-1"
						onClick={() => stopMutation.mutate()}
					>
						{returnsToThread ? "Back to thread" : "Exit"}
					</Button>
				</div>
			</div>
		</>
	)
}
